// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Alert, Modal, Spinner } from '@loa/ui-components';
import {
  relationshipCashflowApi,
  SCENARIO_ASSUMPTION_KEYS,
  type Scenario,
  type ScenarioOverride,
  type ScenarioCompare,
} from '../../api/relationshipCashflowApi';
import {
  financialPositionApi,
  type AccountSummary,
  type FigureOption,
  type TaxClassificationItem,
} from '../../api/financialPositionApi';

const LINE_COLORS = ['#525252', '#2D0A6C', '#1E8E4E', '#C0392B', '#B9770E', '#7C5CFC'];

/**
 * What-if scenario controls for the Relationship Cashflow card (figure-provenance
 * Tranche C): a Base/scenario selector that drives the card's projection, plus
 * Manage (create / delete / dated figure overrides) and Compare (net-worth lines)
 * modals. Renders nothing when scenarios are disabled (the list endpoint 404s → []).
 */
export function ScenarioControls({
  profileId,
  selectedScenarioId,
  onSelect,
}: {
  profileId: string;
  selectedScenarioId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [figures, setFigures] = useState<FigureOption[]>([]);
  const [disposalTargets, setDisposalTargets] = useState<TaxClassificationItem[]>([]);
  const [sweepAccounts, setSweepAccounts] = useState<AccountSummary[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [managing, setManaging] = useState(false);
  const [comparing, setComparing] = useState(false);

  const load = useCallback(() => {
    relationshipCashflowApi
      .listScenarios(profileId)
      .then(setScenarios)
      .finally(() => setLoaded(true));
  }, [profileId]);
  useEffect(load, [load]);

  // Selectable override targets = the relationship's income + expenditure figures.
  useEffect(() => {
    financialPositionApi
      .getFigures(profileId)
      .then(setFigures)
      .catch(() => setFigures([]));
  }, [profileId]);

  // Disposable items (asset / investment / holding) for the "Sell asset" event.
  useEffect(() => {
    financialPositionApi
      .getTaxClassification(profileId)
      .then((tc) => setDisposalTargets(tc.items))
      .catch(() => setDisposalTargets([]));
  }, [profileId]);

  // Asset accounts — the targets for a surplus sweep.
  useEffect(() => {
    financialPositionApi
      .listAccounts(profileId, 'ASSET')
      .then(setSweepAccounts)
      .catch(() => setSweepAccounts([]));
  }, [profileId]);

  // Hide entirely until we know whether scenarios exist (and the feature is on).
  if (loaded && scenarios.length === 0 && !managing) {
    return (
      <button
        type="button"
        data-testid="cashflow-scenario-new"
        onClick={() => setManaging(true)}
        className="rounded-lg border-[1.5px] border-[#BDB8FF] bg-white px-[10px] py-[6px] text-[13px] text-purple-primary hover:bg-lilac-06"
      >
        + What-if scenario
      </button>
    );
  }

  return (
    <div className="flex items-center gap-sm">
      <label className="text-[13px] text-grey-04">
        Scenario
        <select
          data-testid="cashflow-scenario-select"
          value={selectedScenarioId ?? ''}
          onChange={(e) => onSelect(e.target.value === '' ? null : e.target.value)}
          className="ml-[6px] rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px] text-[13px]"
        >
          <option value="">Base</option>
          {scenarios.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        data-testid="cashflow-scenario-manage"
        onClick={() => setManaging(true)}
        className="rounded-lg border-[1.5px] border-[#BDB8FF] bg-white px-[10px] py-[6px] text-[13px] text-purple-primary hover:bg-lilac-06"
      >
        Manage
      </button>
      <button
        type="button"
        data-testid="cashflow-scenario-compare"
        onClick={() => setComparing(true)}
        className="rounded-lg border-[1.5px] border-[#BDB8FF] bg-white px-[10px] py-[6px] text-[13px] text-purple-primary hover:bg-lilac-06"
      >
        Compare
      </button>
      {managing && (
        <ScenarioManageModal
          profileId={profileId}
          scenarios={scenarios}
          figures={figures}
          disposalTargets={disposalTargets}
          sweepAccounts={sweepAccounts}
          onClose={() => setManaging(false)}
          onChanged={() => {
            load();
          }}
          onDeletedSelected={() => onSelect(null)}
        />
      )}
      {comparing && (
        <ScenarioCompareModal
          profileId={profileId}
          scenarios={scenarios}
          onClose={() => setComparing(false)}
        />
      )}
    </div>
  );
}


/**
 * The per-scenario parameter editor — overrides list + figure / assumption /
 * policy override forms + the six event accordions. Shared by the
 * ScenarioManageModal (flag-off Overview path) and the Cashflow Modelling
 * workspace's side panel (cashflow-modelling-tab PRD R13). Owns its own
 * busy/error state; {@code onChanged} fires after every persisted change.
 */
export function ScenarioEditorBody({
  profileId,
  scenario,
  figures,
  disposalTargets,
  sweepAccounts,
  onChanged,
}: {
  profileId: string;
  scenario: Scenario;
  figures: FigureOption[];
  disposalTargets: TaxClassificationItem[];
  sweepAccounts: AccountSummary[];
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [figureRef, setFigureRef] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [amount, setAmount] = useState('');
  const [assumptionKey, setAssumptionKey] = useState('');
  const [assumptionFrom, setAssumptionFrom] = useState('');
  const [assumptionValue, setAssumptionValue] = useState('');
  const [policyRef, setPolicyRef] = useState('');
  const [policySetting, setPolicySetting] = useState('GROWTH');
  const [policyFrom, setPolicyFrom] = useState('');
  const [policyValue, setPolicyValue] = useState('');
  const [policyMode, setPolicyMode] = useState('DELTA');

  // ── Event: add income/spending ──
  const [cfDirection, setCfDirection] = useState<'INCOME' | 'EXPENDITURE'>('EXPENDITURE');
  const [cfLabel, setCfLabel] = useState('');
  const [cfAmount, setCfAmount] = useState('');
  const [cfFrequency, setCfFrequency] = useState('ANNUALLY');
  const [cfFrom, setCfFrom] = useState('');
  const [cfEndYear, setCfEndYear] = useState('');

  // ── Event: add account ──
  const [acctType, setAcctType] = useState('SAVINGS');
  const [acctClass, setAcctClass] = useState<'ASSET' | 'LIABILITY'>('ASSET');
  const [acctBalance, setAcctBalance] = useState('');
  const [acctRate, setAcctRate] = useState('');
  const [acctFrom, setAcctFrom] = useState('');

  // ── Event: add asset ──
  const [assetType, setAssetType] = useState('PROPERTY');
  const [assetName, setAssetName] = useState('');
  const [assetValue, setAssetValue] = useState('');
  const [assetGrowth, setAssetGrowth] = useState('');
  const [assetFrom, setAssetFrom] = useState('');

  // ── Event: sell / dispose ──
  const [disposalRef, setDisposalRef] = useState('');
  const [disposalFrom, setDisposalFrom] = useState('');
  const [disposalProceeds, setDisposalProceeds] = useState('');

  // ── Event: sweep surplus → account ──
  const [sweepRef, setSweepRef] = useState('');
  const [sweepFrom, setSweepFrom] = useState('');

  // ── Event: conditional sweep rule (account → account) ──
  const [ruleFromRef, setRuleFromRef] = useState('');
  const [ruleToRef, setRuleToRef] = useState('');
  const [ruleThreshold, setRuleThreshold] = useState('');
  const [ruleMode, setRuleMode] = useState<'ALL_OVER' | 'FIXED'>('ALL_OVER');
  const [ruleAmount, setRuleAmount] = useState('');
  const [ruleFrom, setRuleFrom] = useState('');

  const policyFigures = figures.filter((f) => f.kind === 'PENSION' || f.kind === 'INVESTMENT');

  const POLICY_KIND_LABEL: Record<string, string> = {
    POLICY_GROWTH: 'growth',
    POLICY_CONTRIBUTION: 'contribution',
    POLICY_VALUE: 'value',
  };

  const yearOf = (validFrom: string): string => validFrom?.slice(0, 4) || validFrom;

  const overrideLabel = (o: ScenarioOverride): string => {
    const p = o.payload ?? {};
    if (o.kind === 'CASHFLOW_ADD') {
      const verb = p.direction === 'INCOME' ? 'Add income' : 'Add spending';
      const label = p.label ? `${p.label} ` : '';
      return `${verb}: ${label}€${p.amount ?? o.amount} from ${yearOf(o.validFrom)}`;
    }
    if (o.kind === 'ACCOUNT_ADD') {
      const name = p.label ?? p.accountType ?? 'account';
      return `Add account: ${name} €${p.balance ?? o.amount} from ${yearOf(o.validFrom)}`;
    }
    if (o.kind === 'ASSET_ADD') {
      const name = p.name ?? p.assetType ?? 'asset';
      return `Add asset: ${name} €${p.value ?? o.amount} from ${yearOf(o.validFrom)}`;
    }
    if (o.kind === 'DISPOSAL') {
      const ref = o.figureRef ?? (p.targetRef as string | undefined);
      return `Sell ${ref ?? 'item'} in ${yearOf(o.validFrom)}`;
    }
    if (o.kind === 'SWEEP_SURPLUS') {
      return `Sweep surplus → ${o.figureRef ?? 'account'} from ${yearOf(o.validFrom)}`;
    }
    if (o.kind === 'SWEEP_RULE') {
      const over = p.mode === 'FIXED' ? `€${p.amount ?? ''}` : `over €${p.thresholdBalance ?? ''}`;
      return `Sweep ${over}: ${p.fromRef ?? ''} → ${p.toRef ?? ''} from ${yearOf(o.validFrom)}`;
    }
    if (o.kind?.startsWith('POLICY_')) {
      const fig = figures.find((f) => f.key === o.figureRef);
      return `${fig?.label ?? o.figureRef} ${POLICY_KIND_LABEL[o.kind] ?? ''} → ${o.amount} from ${o.validFrom}`;
    }
    if (o.kind === 'ASSUMPTION') {
      const meta = SCENARIO_ASSUMPTION_KEYS.find((k) => k.key === o.assumptionKey);
      return `${meta?.label ?? o.assumptionKey} → ${o.amount}${meta?.unit ?? ''} from ${o.validFrom}`;
    }
    const fig = figures.find((f) => f.key === o.figureRef);
    return `${fig?.label ?? o.figureRef} → ${o.amount} from ${o.validFrom}`;
  };

  const addAssumption = (id: string) => {
    if (assumptionKey === '' || assumptionFrom === '' || assumptionValue === '' || Number.isNaN(Number(assumptionValue))) {
      setError('Choose an assumption, a date and a value.');
      return;
    }
    setBusy(true);
    setError(null);
    relationshipCashflowApi
      .addScenarioAssumptionOverride(profileId, id, {
        assumptionKey,
        validFrom: assumptionFrom,
        value: Number(assumptionValue),
      })
      .then(() => {
        setAssumptionKey('');
        setAssumptionFrom('');
        setAssumptionValue('');
        onChanged();
      })
      .catch(() => setError('Could not add the assumption override.'))
      .finally(() => setBusy(false));
  };

  const addPolicy = (id: string) => {
    if (policyRef === '' || policyFrom === '' || policyValue === '' || Number.isNaN(Number(policyValue))) {
      setError('Choose a policy, a setting, a date and a value.');
      return;
    }
    setBusy(true);
    setError(null);
    relationshipCashflowApi
      .addScenarioPolicyOverride(profileId, id, {
        policyRef,
        setting: policySetting,
        validFrom: policyFrom,
        value: Number(policyValue),
        mode: policySetting === 'VALUE' ? policyMode : null,
      })
      .then(() => {
        setPolicyRef('');
        setPolicyFrom('');
        setPolicyValue('');
        onChanged();
      })
      .catch(() => setError('Could not add the policy override.'))
      .finally(() => setBusy(false));
  };

  const addCashflowItem = (id: string) => {
    if (cfLabel.trim() === '' || cfFrom === '' || cfAmount === '' || Number.isNaN(Number(cfAmount))) {
      setError('Enter a label, an amount and a from-date.');
      return;
    }
    setBusy(true);
    setError(null);
    relationshipCashflowApi
      .addScenarioCashflowItem(profileId, id, {
        validFrom: cfFrom,
        direction: cfDirection,
        category: null,
        label: cfLabel.trim(),
        amount: Number(cfAmount),
        frequency: cfFrequency,
        ownerClientId: null,
        endYear: cfEndYear === '' ? null : Number(cfEndYear),
      })
      .then(() => {
        setCfLabel('');
        setCfAmount('');
        setCfFrom('');
        setCfEndYear('');
        onChanged();
      })
      .catch(() => setError('Could not add the income/spending event.'))
      .finally(() => setBusy(false));
  };

  const addAccount = (id: string) => {
    if (acctFrom === '' || acctBalance === '' || Number.isNaN(Number(acctBalance))) {
      setError('Enter a balance and a from-date.');
      return;
    }
    setBusy(true);
    setError(null);
    relationshipCashflowApi
      .addScenarioAccount(profileId, id, {
        validFrom: acctFrom,
        accountType: acctType,
        classification: acctClass,
        balance: Number(acctBalance),
        interestRate: acctRate === '' ? null : Number(acctRate),
        monthlyRepayment: null,
        ownerClientId: null,
        label: null,
      })
      .then(() => {
        setAcctBalance('');
        setAcctRate('');
        setAcctFrom('');
        onChanged();
      })
      .catch(() => setError('Could not add the account event.'))
      .finally(() => setBusy(false));
  };

  const addAsset = (id: string) => {
    if (assetFrom === '' || assetValue === '' || Number.isNaN(Number(assetValue))) {
      setError('Enter a value and a from-date.');
      return;
    }
    setBusy(true);
    setError(null);
    relationshipCashflowApi
      .addScenarioAsset(profileId, id, {
        validFrom: assetFrom,
        assetType,
        name: assetName.trim() === '' ? null : assetName.trim(),
        value: Number(assetValue),
        assumedGrowthRate: assetGrowth === '' ? null : Number(assetGrowth),
        ownerClientId: null,
      })
      .then(() => {
        setAssetName('');
        setAssetValue('');
        setAssetGrowth('');
        setAssetFrom('');
        onChanged();
      })
      .catch(() => setError('Could not add the asset event.'))
      .finally(() => setBusy(false));
  };

  const addDisposal = (id: string) => {
    if (disposalRef === '' || disposalFrom === '') {
      setError('Choose an item to sell and a from-date.');
      return;
    }
    setBusy(true);
    setError(null);
    relationshipCashflowApi
      .addScenarioDisposal(profileId, id, {
        targetRef: disposalRef,
        validFrom: disposalFrom,
        proceeds: disposalProceeds === '' ? null : Number(disposalProceeds),
      })
      .then(() => {
        setDisposalRef('');
        setDisposalFrom('');
        setDisposalProceeds('');
        onChanged();
      })
      .catch(() => setError('Could not add the disposal event.'))
      .finally(() => setBusy(false));
  };

  const addSweep = (id: string) => {
    if (sweepRef === '' || sweepFrom === '') {
      setError('Choose an account and a from-date.');
      return;
    }
    setBusy(true);
    setError(null);
    relationshipCashflowApi
      .addScenarioSweepSurplus(profileId, id, { accountRef: sweepRef, validFrom: sweepFrom })
      .then(() => {
        setSweepRef('');
        setSweepFrom('');
        onChanged();
      })
      .catch(() => setError('Could not add the surplus sweep.'))
      .finally(() => setBusy(false));
  };

  const addRule = (id: string) => {
    if (ruleFromRef === '' || ruleToRef === '' || ruleThreshold === '' || ruleFrom === '') {
      setError('Choose from/to accounts, a threshold and a from-date.');
      return;
    }
    setBusy(true);
    setError(null);
    relationshipCashflowApi
      .addScenarioSweepRule(profileId, id, {
        fromRef: ruleFromRef,
        toRef: ruleToRef,
        thresholdBalance: Number(ruleThreshold),
        mode: ruleMode,
        amount: ruleMode === 'FIXED' && ruleAmount !== '' ? Number(ruleAmount) : null,
        validFrom: ruleFrom,
      })
      .then(() => {
        setRuleFromRef('');
        setRuleToRef('');
        setRuleThreshold('');
        setRuleAmount('');
        setRuleFrom('');
        onChanged();
      })
      .catch(() => setError('Could not add the sweep rule.'))
      .finally(() => setBusy(false));
  };

  const addOverride = (id: string) => {
    if (figureRef.trim() === '' || validFrom === '' || amount === '' || Number.isNaN(Number(amount))) {
      setError('Enter a figure, a date and an amount.');
      return;
    }
    setBusy(true);
    setError(null);
    relationshipCashflowApi
      .addScenarioOverride(profileId, id, {
        figureRef: figureRef.trim(),
        validFrom,
        amount: Number(amount),
        frequency: null,
      })
      .then(() => {
        setFigureRef('');
        setValidFrom('');
        setAmount('');
        onChanged();
      })
      .catch(() => setError('Could not add the override.'))
      .finally(() => setBusy(false));
  };

  const removeOverride = (id: string, overrideId: string) => {
    setBusy(true);
    relationshipCashflowApi
      .removeScenarioOverride(profileId, id, overrideId)
      .then(onChanged)
      .catch(() => setError('Could not remove the override.'))
      .finally(() => setBusy(false));
  };

  return (
    <>
      {error && <Alert variant="error">{error}</Alert>}
                <div className="mt-sm flex flex-col gap-sm">
                  {scenario.overrides.map((o) => (
                    <div key={o.id} className="flex items-center justify-between text-[13px]">
                      <span className="text-black">{overrideLabel(o)}</span>
                      <button
                        type="button"
                        data-testid={`override-delete-${o.id}`}
                        onClick={() => removeOverride(scenario.id, o.id)}
                        disabled={busy}
                        className="text-[#C0392B] hover:underline disabled:opacity-60"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <div className="flex flex-wrap items-end gap-sm">
                    <label className="text-body-04 text-grey-04">
                      Figure
                      <select
                        data-testid="override-figure"
                        value={figureRef}
                        onChange={(e) => setFigureRef(e.target.value)}
                        className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                      >
                        <option value="">Select…</option>
                        <optgroup label="Income">
                          {figures.filter((f) => f.kind === 'INCOME').map((f) => (
                            <option key={f.key} value={f.key}>
                              {f.label}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Expenditure">
                          {figures.filter((f) => f.kind === 'EXPENDITURE').map((f) => (
                            <option key={f.key} value={f.key}>
                              {f.label}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                    </label>
                    <label className="text-body-04 text-grey-04">
                      From
                      <input
                        type="date"
                        data-testid="override-date"
                        value={validFrom}
                        onChange={(e) => setValidFrom(e.target.value)}
                        className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                      />
                    </label>
                    <label className="text-body-04 text-grey-04">
                      Amount
                      <input
                        type="number"
                        data-testid="override-amount"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                      />
                    </label>
                    <button
                      type="button"
                      data-testid={`override-add-${scenario.id}`}
                      onClick={() => addOverride(scenario.id)}
                      disabled={busy}
                      className="rounded-lg bg-purple-primary px-[10px] py-[5px] text-[13px] text-white disabled:opacity-60"
                    >
                      Add figure override
                    </button>
                  </div>
                  <div className="flex flex-wrap items-end gap-sm">
                    <label className="text-body-04 text-grey-04">
                      Assumption
                      <select
                        data-testid="assumption-key"
                        value={assumptionKey}
                        onChange={(e) => setAssumptionKey(e.target.value)}
                        className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                      >
                        <option value="">Select…</option>
                        {SCENARIO_ASSUMPTION_KEYS.map((k) => (
                          <option key={k.key} value={k.key}>
                            {k.label} ({k.unit})
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-body-04 text-grey-04">
                      From
                      <input
                        type="date"
                        data-testid="assumption-date"
                        value={assumptionFrom}
                        onChange={(e) => setAssumptionFrom(e.target.value)}
                        className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                      />
                    </label>
                    <label className="text-body-04 text-grey-04">
                      Value
                      <input
                        type="number"
                        data-testid="assumption-value"
                        value={assumptionValue}
                        onChange={(e) => setAssumptionValue(e.target.value)}
                        className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                      />
                    </label>
                    <button
                      type="button"
                      data-testid={`assumption-add-${scenario.id}`}
                      onClick={() => addAssumption(scenario.id)}
                      disabled={busy}
                      className="rounded-lg bg-purple-primary px-[10px] py-[5px] text-[13px] text-white disabled:opacity-60"
                    >
                      Add assumption override
                    </button>
                  </div>
                  {policyFigures.length > 0 && (
                    <div className="flex flex-wrap items-end gap-sm">
                      <label className="text-body-04 text-grey-04">
                        Pension / Investment
                        <select
                          data-testid="policy-ref"
                          value={policyRef}
                          onChange={(e) => setPolicyRef(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        >
                          <option value="">Select…</option>
                          {policyFigures.map((f) => (
                            <option key={f.key} value={f.key}>
                              {f.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="text-body-04 text-grey-04">
                        Setting
                        <select
                          data-testid="policy-setting"
                          value={policySetting}
                          onChange={(e) => setPolicySetting(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        >
                          <option value="GROWTH">Growth rate (%)</option>
                          <option value="CONTRIBUTION">Contribution (€/yr)</option>
                          <option value="VALUE">Value (€)</option>
                        </select>
                      </label>
                      {policySetting === 'VALUE' && (
                        <label className="text-body-04 text-grey-04">
                          Mode
                          <select
                            data-testid="policy-mode"
                            value={policyMode}
                            onChange={(e) => setPolicyMode(e.target.value)}
                            className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                          >
                            <option value="DELTA">Add lump sum</option>
                            <option value="ABSOLUTE">Revalue to</option>
                          </select>
                        </label>
                      )}
                      <label className="text-body-04 text-grey-04">
                        From
                        <input
                          type="date"
                          data-testid="policy-date"
                          value={policyFrom}
                          onChange={(e) => setPolicyFrom(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <label className="text-body-04 text-grey-04">
                        Value
                        <input
                          type="number"
                          data-testid="policy-value"
                          value={policyValue}
                          onChange={(e) => setPolicyValue(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <button
                        type="button"
                        data-testid={`policy-add-${scenario.id}`}
                        onClick={() => addPolicy(scenario.id)}
                        disabled={busy}
                        className="rounded-lg bg-purple-primary px-[10px] py-[5px] text-[13px] text-white disabled:opacity-60"
                      >
                        Add policy override
                      </button>
                    </div>
                  )}

                  {/* ── Event: add income/spending ── */}
                  <details data-testid={`event-cashflow-${scenario.id}`} className="rounded-lg border border-[#E1E1E1] p-sm">
                    <summary className="cursor-pointer text-body-04 font-medium text-purple-primary">
                      Add income/spending
                    </summary>
                    <div className="mt-sm flex flex-wrap items-end gap-sm">
                      <label className="text-body-04 text-grey-04">
                        Direction
                        <select
                          data-testid="cf-direction"
                          value={cfDirection}
                          onChange={(e) => setCfDirection(e.target.value as 'INCOME' | 'EXPENDITURE')}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        >
                          <option value="EXPENDITURE">Spending</option>
                          <option value="INCOME">Income</option>
                        </select>
                      </label>
                      <label className="text-body-04 text-grey-04">
                        Label
                        <input
                          type="text"
                          data-testid="cf-label"
                          value={cfLabel}
                          onChange={(e) => setCfLabel(e.target.value)}
                          placeholder="e.g. School fees"
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <label className="text-body-04 text-grey-04">
                        Amount
                        <input
                          type="number"
                          data-testid="cf-amount"
                          value={cfAmount}
                          onChange={(e) => setCfAmount(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <label className="text-body-04 text-grey-04">
                        Frequency
                        <select
                          data-testid="cf-frequency"
                          value={cfFrequency}
                          onChange={(e) => setCfFrequency(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        >
                          <option value="ANNUALLY">Annually</option>
                          <option value="MONTHLY">Monthly</option>
                        </select>
                      </label>
                      <label className="text-body-04 text-grey-04">
                        From
                        <input
                          type="date"
                          data-testid="cf-date"
                          value={cfFrom}
                          onChange={(e) => setCfFrom(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <label className="text-body-04 text-grey-04">
                        End year
                        <input
                          type="number"
                          data-testid="cf-end-year"
                          value={cfEndYear}
                          onChange={(e) => setCfEndYear(e.target.value)}
                          placeholder="optional"
                          className="mt-[2px] block w-[96px] rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <button
                        type="button"
                        data-testid={`cf-add-${scenario.id}`}
                        onClick={() => addCashflowItem(scenario.id)}
                        disabled={busy}
                        className="rounded-lg bg-purple-primary px-[10px] py-[5px] text-[13px] text-white disabled:opacity-60"
                      >
                        Add income/spending
                      </button>
                    </div>
                  </details>

                  {/* ── Event: add account ── */}
                  <details data-testid={`event-account-${scenario.id}`} className="rounded-lg border border-[#E1E1E1] p-sm">
                    <summary className="cursor-pointer text-body-04 font-medium text-purple-primary">
                      Add account
                    </summary>
                    <div className="mt-sm flex flex-wrap items-end gap-sm">
                      <label className="text-body-04 text-grey-04">
                        Account type
                        <select
                          data-testid="acct-type"
                          value={acctType}
                          onChange={(e) => setAcctType(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        >
                          <option value="SAVINGS">Savings</option>
                          <option value="CURRENT">Current</option>
                          <option value="LOAN">Loan</option>
                          <option value="CREDIT_CARD">Credit card</option>
                          <option value="MORTGAGE">Mortgage</option>
                        </select>
                      </label>
                      <label className="text-body-04 text-grey-04">
                        Classification
                        <select
                          data-testid="acct-class"
                          value={acctClass}
                          onChange={(e) => setAcctClass(e.target.value as 'ASSET' | 'LIABILITY')}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        >
                          <option value="ASSET">Asset</option>
                          <option value="LIABILITY">Liability</option>
                        </select>
                      </label>
                      <label className="text-body-04 text-grey-04">
                        Balance
                        <input
                          type="number"
                          data-testid="acct-balance"
                          value={acctBalance}
                          onChange={(e) => setAcctBalance(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <label className="text-body-04 text-grey-04">
                        Interest rate
                        <input
                          type="number"
                          data-testid="acct-rate"
                          value={acctRate}
                          onChange={(e) => setAcctRate(e.target.value)}
                          placeholder="optional %"
                          className="mt-[2px] block w-[110px] rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <label className="text-body-04 text-grey-04">
                        From
                        <input
                          type="date"
                          data-testid="acct-date"
                          value={acctFrom}
                          onChange={(e) => setAcctFrom(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <button
                        type="button"
                        data-testid={`acct-add-${scenario.id}`}
                        onClick={() => addAccount(scenario.id)}
                        disabled={busy}
                        className="rounded-lg bg-purple-primary px-[10px] py-[5px] text-[13px] text-white disabled:opacity-60"
                      >
                        Add account
                      </button>
                    </div>
                  </details>

                  {/* ── Event: add asset ── */}
                  <details data-testid={`event-asset-${scenario.id}`} className="rounded-lg border border-[#E1E1E1] p-sm">
                    <summary className="cursor-pointer text-body-04 font-medium text-purple-primary">
                      Add asset
                    </summary>
                    <div className="mt-sm flex flex-wrap items-end gap-sm">
                      <label className="text-body-04 text-grey-04">
                        Asset type
                        <select
                          data-testid="asset-type"
                          value={assetType}
                          onChange={(e) => setAssetType(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        >
                          <option value="PROPERTY">Property</option>
                          <option value="COLLECTIBLE">Collectible</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </label>
                      <label className="text-body-04 text-grey-04">
                        Name
                        <input
                          type="text"
                          data-testid="asset-name"
                          value={assetName}
                          onChange={(e) => setAssetName(e.target.value)}
                          placeholder="e.g. Holiday Home"
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <label className="text-body-04 text-grey-04">
                        Value
                        <input
                          type="number"
                          data-testid="asset-value"
                          value={assetValue}
                          onChange={(e) => setAssetValue(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <label className="text-body-04 text-grey-04">
                        Growth rate
                        <input
                          type="number"
                          data-testid="asset-growth"
                          value={assetGrowth}
                          onChange={(e) => setAssetGrowth(e.target.value)}
                          placeholder="optional %"
                          className="mt-[2px] block w-[110px] rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <label className="text-body-04 text-grey-04">
                        From
                        <input
                          type="date"
                          data-testid="asset-date"
                          value={assetFrom}
                          onChange={(e) => setAssetFrom(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <button
                        type="button"
                        data-testid={`asset-add-${scenario.id}`}
                        onClick={() => addAsset(scenario.id)}
                        disabled={busy}
                        className="rounded-lg bg-purple-primary px-[10px] py-[5px] text-[13px] text-white disabled:opacity-60"
                      >
                        Add asset
                      </button>
                    </div>
                  </details>

                  {/* ── Event: sell asset (CGT) ── */}
                  <details data-testid={`event-disposal-${scenario.id}`} className="rounded-lg border border-[#E1E1E1] p-sm">
                    <summary className="cursor-pointer text-body-04 font-medium text-purple-primary">
                      Sell asset (CGT)
                    </summary>
                    <div className="mt-sm flex flex-wrap items-end gap-sm">
                      <label className="text-body-04 text-grey-04">
                        Item
                        <select
                          data-testid="disposal-ref"
                          value={disposalRef}
                          onChange={(e) => setDisposalRef(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        >
                          <option value="">Select…</option>
                          {disposalTargets.map((t) => (
                            <option key={`${t.kind}:${t.id}`} value={`${t.kind.toLowerCase()}:${t.id}`}>
                              {t.label} ({t.owner})
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="text-body-04 text-grey-04">
                        From
                        <input
                          type="date"
                          data-testid="disposal-date"
                          value={disposalFrom}
                          onChange={(e) => setDisposalFrom(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <label className="text-body-04 text-grey-04">
                        Proceeds
                        <input
                          type="number"
                          data-testid="disposal-proceeds"
                          value={disposalProceeds}
                          onChange={(e) => setDisposalProceeds(e.target.value)}
                          placeholder="optional"
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <button
                        type="button"
                        data-testid={`disposal-add-${scenario.id}`}
                        onClick={() => addDisposal(scenario.id)}
                        disabled={busy}
                        className="rounded-lg bg-purple-primary px-[10px] py-[5px] text-[13px] text-white disabled:opacity-60"
                      >
                        Sell asset
                      </button>
                    </div>
                  </details>

                  {/* ── Event: sweep surplus → account ── */}
                  <details data-testid={`event-sweep-${scenario.id}`} className="rounded-lg border border-[#E1E1E1] p-sm">
                    <summary className="cursor-pointer text-body-04 font-medium text-purple-primary">
                      Sweep surplus → account
                    </summary>
                    <div className="mt-sm flex flex-wrap items-end gap-sm">
                      <label className="text-body-04 text-grey-04">
                        Account
                        <select
                          data-testid="sweep-ref"
                          value={sweepRef}
                          onChange={(e) => setSweepRef(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        >
                          <option value="">Select…</option>
                          {sweepAccounts.map((a) => (
                            <option key={a.id} value={`account:${a.id}`}>
                              {a.institutionName ?? a.accountType}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="text-body-04 text-grey-04">
                        From
                        <input
                          type="date"
                          data-testid="sweep-date"
                          value={sweepFrom}
                          onChange={(e) => setSweepFrom(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <button
                        type="button"
                        data-testid={`sweep-add-${scenario.id}`}
                        onClick={() => addSweep(scenario.id)}
                        disabled={busy}
                        className="rounded-lg bg-purple-primary px-[10px] py-[5px] text-[13px] text-white disabled:opacity-60"
                      >
                        Sweep surplus
                      </button>
                    </div>
                  </details>

                  {/* ── Event: conditional sweep rule (account → account) ── */}
                  <details data-testid={`event-sweeprule-${scenario.id}`} className="rounded-lg border border-[#E1E1E1] p-sm">
                    <summary className="cursor-pointer text-body-04 font-medium text-purple-primary">
                      Sweep rule (account → account)
                    </summary>
                    <div className="mt-sm flex flex-wrap items-end gap-sm">
                      <label className="text-body-04 text-grey-04">
                        From
                        <select
                          data-testid="rule-from"
                          value={ruleFromRef}
                          onChange={(e) => setRuleFromRef(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        >
                          <option value="">Select…</option>
                          {sweepAccounts.map((a) => (
                            <option key={a.id} value={`account:${a.id}`}>
                              {a.institutionName ?? a.accountType}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="text-body-04 text-grey-04">
                        To
                        <select
                          data-testid="rule-to"
                          value={ruleToRef}
                          onChange={(e) => setRuleToRef(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        >
                          <option value="">Select…</option>
                          {sweepAccounts.map((a) => (
                            <option key={a.id} value={`account:${a.id}`}>
                              {a.institutionName ?? a.accountType}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="text-body-04 text-grey-04">
                        Mode
                        <select
                          data-testid="rule-mode"
                          value={ruleMode}
                          onChange={(e) => setRuleMode(e.target.value as 'ALL_OVER' | 'FIXED')}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        >
                          <option value="ALL_OVER">All over threshold</option>
                          <option value="FIXED">Fixed amount</option>
                        </select>
                      </label>
                      <label className="text-body-04 text-grey-04">
                        Threshold
                        <input
                          type="number"
                          data-testid="rule-threshold"
                          value={ruleThreshold}
                          onChange={(e) => setRuleThreshold(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      {ruleMode === 'FIXED' && (
                        <label className="text-body-04 text-grey-04">
                          Amount
                          <input
                            type="number"
                            data-testid="rule-amount"
                            value={ruleAmount}
                            onChange={(e) => setRuleAmount(e.target.value)}
                            className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                          />
                        </label>
                      )}
                      <label className="text-body-04 text-grey-04">
                        From
                        <input
                          type="date"
                          data-testid="rule-date"
                          value={ruleFrom}
                          onChange={(e) => setRuleFrom(e.target.value)}
                          className="mt-[2px] block rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px]"
                        />
                      </label>
                      <button
                        type="button"
                        data-testid={`rule-add-${scenario.id}`}
                        onClick={() => addRule(scenario.id)}
                        disabled={busy}
                        className="rounded-lg bg-purple-primary px-[10px] py-[5px] text-[13px] text-white disabled:opacity-60"
                      >
                        Add sweep rule
                      </button>
                    </div>
                  </details>
                </div>
    </>
  );
}

export function ScenarioManageModal({
  profileId,
  scenarios,
  figures,
  disposalTargets,
  sweepAccounts,
  onClose,
  onChanged,
  onDeletedSelected,
}: {
  profileId: string;
  scenarios: Scenario[];
  figures: FigureOption[];
  disposalTargets: TaxClassificationItem[];
  sweepAccounts: AccountSummary[];
  onClose: () => void;
  onChanged: () => void;
  onDeletedSelected: () => void;
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const create = () => {
    if (busy || name.trim() === '') {
      setError('Enter a scenario name.');
      return;
    }
    setBusy(true);
    setError(null);
    relationshipCashflowApi
      .createScenario(profileId, { name: name.trim(), description: null })
      .then(() => {
        setName('');
        onChanged();
      })
      .catch(() => setError('Could not create the scenario.'))
      .finally(() => setBusy(false));
  };

  const remove = (id: string) => {
    setBusy(true);
    relationshipCashflowApi
      .deleteScenario(profileId, id)
      .then(() => {
        onDeletedSelected();
        onChanged();
      })
      .catch(() => setError('Could not delete the scenario.'))
      .finally(() => setBusy(false));
  };

  return (
    <Modal open onClose={onClose} size="large">
      <Modal.Header icon="settings gear" title="What-if scenarios" onClose={onClose} />
      <Modal.Content>
        <div data-testid="scenario-manage-modal" className="flex flex-col gap-[12px]">
          {error && <Alert variant="error">{error}</Alert>}
          <div className="flex items-end gap-sm">
            <label className="text-[13px] text-black">
              New scenario
              <input
                type="text"
                data-testid="scenario-new-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Retire at 60"
                className="mt-xs block rounded-lg border-[1.5px] border-[#BDB8FF] px-[10px] py-[6px]"
              />
            </label>
            <button
              type="button"
              data-testid="scenario-create"
              onClick={create}
              disabled={busy}
              className="rounded-lg bg-purple-primary px-[12px] py-[6px] text-body-03 text-white disabled:opacity-60"
            >
              Create
            </button>
          </div>

          {scenarios.map((s) => (
            <div key={s.id} className="rounded-button border border-[#E1E1E1] p-[12px]">
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  data-testid={`scenario-toggle-${s.id}`}
                  onClick={() => setOpenId(openId === s.id ? null : s.id)}
                  className="text-body-03 font-medium text-black"
                >
                  {s.name} <span className="text-grey-04">({s.overrides.length})</span>
                </button>
                <button
                  type="button"
                  data-testid={`scenario-delete-${s.id}`}
                  onClick={() => remove(s.id)}
                  disabled={busy}
                  className="text-[13px] text-[#C0392B] hover:underline disabled:opacity-60"
                >
                  Delete
                </button>
              </div>
              {openId === s.id && (
                <ScenarioEditorBody
                  profileId={profileId}
                  scenario={s}
                  figures={figures}
                  disposalTargets={disposalTargets}
                  sweepAccounts={sweepAccounts}
                  onChanged={onChanged}
                />
              )}
            </div>
          ))}
        </div>
      </Modal.Content>
    </Modal>
  );
}


export function ScenarioCompareModal({
  profileId,
  scenarios,
  onClose,
}: {
  profileId: string;
  scenarios: Scenario[];
  onClose: () => void;
}) {
  const [data, setData] = useState<ScenarioCompare | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    relationshipCashflowApi
      .compareScenarios(profileId, scenarios.map((s) => s.id))
      .then(setData)
      .catch(() => setError('Could not load the comparison.'));
  }, [profileId, scenarios]);

  // Merge the lines into one row per year keyed by line name (net worth).
  const rows: Array<Record<string, number>> = [];
  if (data) {
    const years = data.lines[0]?.netWorth.map((p) => p.year) ?? [];
    years.forEach((year, i) => {
      const row: Record<string, number> = { year };
      data.lines.forEach((l) => {
        row[l.name] = l.netWorth[i]?.value ?? 0;
      });
      rows.push(row);
    });
  }

  return (
    <Modal open onClose={onClose} size="large">
      <Modal.Header icon="chart" title="Compare scenarios — net worth" onClose={onClose} />
      <Modal.Content>
        <div data-testid="scenario-compare-modal">
          {error && <Alert variant="error">{error}</Alert>}
          {!data && !error && <Spinner />}
          {data && (
            <ResponsiveContainer width="100%" height={320} minWidth={0}>
              <LineChart data={rows} margin={{ left: 24, right: 16, top: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
                <XAxis dataKey="year" tick={{ fontSize: 12 }} tickFormatter={(v) => `Y${v}`} />
                <YAxis tick={{ fontSize: 12 }} width={72} />
                <Tooltip />
                <Legend />
                {data.lines.map((l, i) => (
                  <Line
                    key={l.name}
                    type="monotone"
                    dataKey={l.name}
                    stroke={LINE_COLORS[i % LINE_COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
}
